import { MapPin, Mail, Phone, Clock, Navigation } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { Reveal } from "@/components/Reveal";
import { ContactForm } from "@/components/ContactForm";
import { MateriauxLigne } from "@/components/MateriauxOrigine";
import { CONTACT_INFO } from "@/data/site";
import { useSeo, breadcrumb } from "@/lib/seo";
import { SocialLinks } from "@/components/SocialLinks";
import { useLocale } from "@/context/LocaleContext";

const SUJETS_URL = { "tambour-sur-mesure": "Commande tambour", "creation-individuelle": "Création individuelle", stage: "Stage de création", soin: "Soin énergétique", "lieu-ancien": "Création sur un lieu ancien", consultation: "Consultation holistique", "devis-lieu": "Harmonisation de lieu" };
const TITRES_URL = { "tambour-sur-mesure": "Imaginer votre tambour", soin: "Je ne sais pas quel soin choisir", "lieu-ancien": "Organiser une création privée sur un lieu ancien", consultation: "Me contacter pour une consultation", "devis-lieu": "Demander un devis pour un lieu" };
const MESSAGES_URL = { soin: "Je ne sais pas quel soin choisir. Voici ce que je vis en ce moment : ", consultation: "Je souhaite prendre rendez-vous pour une consultation. Format souhaité (question ciblée 15 min, 45 min à distance, 60 min à Orbe) et disponibilités : ", "devis-lieu": "Je souhaite un devis pour l'harmonisation d'un lieu. Type de lieu, nombre de pièces / surface, adresse et ressentis : " };

const Contact = () => {
  const { locale, t } = useLocale();
  const [params] = useSearchParams();
  const sujetPreset = SUJETS_URL[params.get("sujet")];
  useSeo({
    title: locale === "en" ? "Contact — Entre Les Mondes, workshop in Orbe (VD), Switzerland" : "Contact — Entre les Mondes, atelier à Orbe (VD), Suisse romande",
    description: locale === "en" ? "Write to Ludovic Isoz or come to the workshop: Rue Pierre Viret 8, 1350 Orbe. Telephone, WhatsApp, email — reply within 48 hours." : "Écrire à Ludovic Isoz ou venir à l'atelier : Rue Pierre Viret 8, 1350 Orbe. Téléphone, WhatsApp, courriel — réponse sous 48 h.",
    jsonLd: [breadcrumb([["Accueil", "/"], ["Contact", "/contact"]]), { "@type": "ContactPage", mainEntity: { "@id": "https://entrelesmondes.net/#organisation" } }],
  });

  return (
    <div>
      <section className="bg-earth grain text-sand relative">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-44 pb-20 relative z-[2]">
          <Reveal>
            <p className="eyebrow mb-6 text-sand/70">Contact</p>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-tight max-w-3xl">
              {t("Une question ? Écrivez, appelez, passez.")}
            </h1>
          </Reveal>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28 grid lg:grid-cols-12 gap-14">
        <Reveal className="lg:col-span-5">
          <div className="space-y-8">
            {[
              { icon: MapPin, label: "L'atelier", valeur: locale === "en" ? "Entre Les Mondes, Rue Pierre Viret 8, 1350 Orbe — Switzerland" : CONTACT_INFO.adresse },
              { icon: Mail, label: "Courriel", valeur: CONTACT_INFO.email, href: `mailto:${CONTACT_INFO.email}` },
              { icon: Phone, label: "Téléphone", valeur: CONTACT_INFO.telephone, href: "tel:+41794606595" },
              { icon: Clock, label: "Réponse", valeur: "Sous 48 h ouvrées, avec soin" },
            ].map(({ icon: Icon, label, valeur, href }) => (
              <div key={label} className="flex gap-5 items-start" data-testid={`contact-info-${label.toLowerCase()}`}>
                <span className="p-3 rounded-full border border-wood/25 text-terra shrink-0"><Icon className="w-4 h-4" /></span>
                <div>
                  <p className="font-mono text-xs uppercase tracking-[0.2em] text-fumee mb-1">{t(label)}</p>
                  {href ? (
                    <a href={href} className="text-bark link-line hover:text-terra transition-colors">{valeur}</a>
                  ) : (
                    <p className="text-bark">{t(valeur)}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
          <div className="mt-10 rounded-2xl overflow-hidden border border-sand-dark aspect-[4/3]" data-testid="contact-map">
            <iframe
              title="Carte — Entre les Mondes, Orbe"
              src={CONTACT_INFO.mapEmbed}
              className="w-full h-full grayscale-[40%] sepia-[15%]"
              loading="lazy"
            />
          </div>
          <a href={CONTACT_INFO.itineraire} target="_blank" rel="noopener noreferrer" data-testid="contact-itineraire"
            className="mt-5 inline-flex items-center gap-3 px-6 py-3 rounded-full border border-wood/30 text-bark text-sm hover:border-terra hover:text-terra transition-colors duration-300">
            <Navigation className="w-4 h-4" /> {t("Itinéraire vers l'atelier")}
          </a>
          <p className="eyebrow mt-10 mb-4">{t("Nous suivre")}</p>
          <SocialLinks dark={false} withLabels />
        </Reveal>
        <Reveal delay={0.15} className="lg:col-span-7">
          <div className="bg-card border border-sand-dark rounded-2xl p-8 lg:p-12">
            <ContactForm titre={TITRES_URL[params.get("sujet")] || "Votre message"} sujetPreset={sujetPreset} messagePreset={MESSAGES_URL[params.get("sujet")]} />
            {params.get("sujet") === "tambour-sur-mesure" && <MateriauxLigne className="mt-8 pt-6 border-t border-sand-dark" testid="materiaux-ligne-sur-mesure" />}
          </div>
        </Reveal>
      </section>
    </div>
  );
};

export default Contact;
